import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    collectionGroup, 
    query, 
    where, 
    onSnapshot, 
    doc, 
    updateDoc, 
    serverTimestamp 
} from "firebase/firestore";

// CORRECCIÓN: Rutas relativas recalibradas de forma precisa a la profundidad de la carpeta PeriodoCorto
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth'; 

import { useTableState } from '../hooks/useTableState';
import { useDataHydration } from '../hooks/useDataHydration';

/**
 * usePeriodoCortoTable - Hook de negocio unificado para el Tablero de Periodos Cortos / Temporales.
 */
export const usePeriodoCortoTable = () => {
    const { user, profile } = useAuth();
    
    // --- ESTADO DE BASE DE DATOS CRUDA ---
    const [polizas, setPolizas] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE CONTROL DE MODALES ---
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showRenewModal, setShowRenewModal] = useState(false);
    const [showDebtModal, setShowDebtModal] = useState(false);
    const [showFichaModal, setShowFichaModal] = useState(false); 
    const [activePoliza, setActivePoliza] = useState(null);

    // REFS DE CONCURRENCIA
    const activeItemPathRef = useRef(null);

    // --- 1. HOOK DE HIDRATACIÓN EN BACKGROUND (useDataHydration) ---
    const { clientsCache } = useDataHydration(polizas);

    // --- 2. LISTENER EN TIEMPO REAL CON REGLAS DE FILTRADO EXCLUSIVO ---
    useEffect(() => {
        const today = new Date(); today.setUTCHours(0, 0, 0, 0); 
        
        // Ventana temporal de vencimientos: 15 días atrás a 60 días adelante
        const thirtyAgoStr = new Date(new Date().setDate(today.getDate() - 15)).toISOString().split('T')[0];
        const sixtyFromNowStr = new Date(new Date().setDate(today.getDate() + 60)).toISOString().split('T')[0];
        
        const q = query(
            collectionGroup(db, 'polizas'), 
            where('estadoEmision', 'in', ['NO INICIADA', 'VIGENTE', 'VENCIDA'])
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const rawData = snapshot.docs.map(docSnap => ({ 
                id: docSnap.id, 
                path: docSnap.ref.path, 
                clienteId: docSnap.ref.path.split('/')[3], 
                ...docSnap.data() 
            }));

            const processedList = [];
            const estadosFinalizados = ['RENOVADA', 'NO RENUEVA', 'ANULADA'];
            const subriesgosCaucionPermitidos = [
                'ALQUILER PARTICULAR', 'ALQUILER COMERCIAL', 'ADMINISTRADOR DE SOCIEDADES (IGJ)', 
                'AMBIENTAL', 'EMPRESAS DE VIAJES Y TURISMO', 'EJERCICIO DE LA ACTIVIDAD Y/O PROFESIÓN'
            ];

            rawData.forEach(p => {
                if (estadosFinalizados.includes(p.estadoGestionRenovacion)) return;

                const esPeriodoCorto = p.tipoFacturacion?.toUpperCase() === 'PERIODO CORTO';
                const esTemporal = p.tipoRenovacion?.toUpperCase() === 'TEMPORAL';

                if (esPeriodoCorto || esTemporal) {
                    // Filtrado de subriesgos Caución
                    if ((p.riesgo || '').toUpperCase() === 'CAUCION') {
                        const normSub = (p.subriesgo || '').normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();
                        if (!subriesgosCaucionPermitidos.includes(normSub)) return;
                    }

                    // Ventana temporal
                    if (p.finVigencia && p.finVigencia >= thirtyAgoStr && p.finVigencia <= sixtyFromNowStr) {
                        processedList.push({ 
                            ...p, 
                            vencimientoDate: p.finVigencia 
                        });
                    }
                }
            });

            setPolizas(processedList);
            setLoading(false);
        }, (error) => console.error("Error cargando polizas periodo corto:", error));

        return () => unsubscribe();
    }, []);

    // --- 3. MAPEO E HISTORIAL DE ENTIDADES (WATERFALL FALLBACK) ---
    const itemsTable = useMemo(() => {
        return polizas.map(p => {
            const c = clientsCache[p.clienteId] || {};
            let displayNombre = `${(p.clienteNombre || '').trim()} ${(p.clienteApellido || '').trim()}`.trim();
            if (!displayNombre) {
                if (c.persona === 'JURIDICA') displayNombre = (c.nombre || '').trim().toUpperCase();
                else displayNombre = `${c.nombre || ''} ${c.apellido || ''}`.trim().toUpperCase();
            }
            if (!displayNombre) displayNombre = 'S/D';
            
            let displayDoc = p.clienteCuit || p.clienteDni || p.cuit || p.dni;
            if (!displayDoc) displayDoc = c.cuit || c.dni || c.dniCuit || '-';

            // Determinar si es la última refacturación para advertencias en pantalla
            let esUltimaRefac = false;
            if (p.fechaRefacturacion && p.fechaVencimiento) {
                const fRefac = new Date(p.fechaRefacturacion);
                const fVenc = new Date(p.fechaVencimiento);
                const diffTime = fVenc.getTime() - fRefac.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                if (diffDays > 0 && diffDays <= 31) {
                    esUltimaRefac = true;
                }
            }

            return { 
                ...p, 
                displayNombre, 
                displayDoc, 
                esUltimaRefac,
                canalNombreUnified: p.canalNombre || p.subcanal || '-',
                estadoGestion: p.estadoGestionRenovacion || 'PENDIENTE'
            };
        });
    }, [polizas, clientsCache]);

    // --- 4. PREVENCIÓN DE PARPADEO (FUC) ---
    const needsHydration = useMemo(() => {
        if (polizas.length === 0) return false;
        return polizas.some(p => !clientsCache[p.clienteId]);
    }, [polizas, clientsCache]);

    // --- 5. HOOK DE CONTROL SÍNCRONO DE TABLAS DE GESTIÓN (useTableState) ---
    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'vencimientoDate', direction: 'ascending' },
        initialColumns: {
            vencimientoDate: true, nPoliza: true, compania: true,
            displayNombre: true, displayDoc: true, riesgo: true, formaPago: true,
            estadoGestion: true, gestionadoPor: true, canal: true, vendedor: true
        },
        recordsPerPage: 100
    });

    // Desbloqueo manual/automático
    const unlockPoliza = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { console.error("Error al desbloquear periodo corto:", e); }
    }, []);

    // --- 6. INTERCEPTOR DE ACCIONES ---
    const handleAction = useCallback(async (poliza, type) => {
        setActivePoliza(poliza);
        if (type === 'ficha') { setShowFichaModal(true); return; }
        if (type === 'view') { setShowDetailModal(true); return; }
        
        if (['edit', 'renew'].includes(type)) {
            activeItemPathRef.current = poliza.path; 
            if (type === 'edit') setShowEditModal(true);
            if (type === 'renew') setShowRenewModal(true);

            await updateDoc(doc(db, poliza.path), { 
                bloqueo: { 
                    userId: user?.uid || 'sys', 
                    userName: profile?.userName || 'Usuario',
                    timestamp: serverTimestamp() 
                } 
            }).catch(e => {
                console.error("Error al bloquear:", e);
                activeItemPathRef.current = null;
            });
        }
    }, [user, profile]);

    const handleShowDebt = useCallback((poliza) => {
        setActivePoliza(poliza);
        setShowDebtModal(true);
    }, []);

    return {
        loading,
        needsHydration,
        clientsCache,
        activePoliza,
        showDetailModal,
        showEditModal,
        showRenewModal,
        showDebtModal,
        showFichaModal,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowDetailModal,
        setShowEditModal,
        setShowRenewModal,
        setShowDebtModal,
        setShowFichaModal,
        setActivePoliza,
        handleAction,
        handleShowDebt,
        unlockPoliza
    };
};